// src/App.tsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useLocation, useNavigate, useParams, matchPath } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { apiScopes } from "@/authConfig";
import Layout from '@/components/Layout';
import { RequireAnySolutionAccess, RequireSolutionAccess } from '@/components/RequireSolutionAccess';
import { RoleAccessProvider } from '@/contexts/RoleAccessContext';
import { OobSessionProvider } from '@/contexts/OobSessionContext';
import { useTheme } from './hooks/useTheme';
import Home from '@/pages/Home/Home';
import MyActivityLog from './pages/MyActivityLog/MyActivityLog';
import MyGroups from '@/pages/MyGroups/MyGroups';
import ResourcesPage from "./pages/Resources/ResourcesPage";
import ResourceCompliancePage from './pages/ResourceCompliance/ResourceCompliancePage';
import ResourceHealthDemoPage from './pages/ResourceCompliance/ResourceHealthDemoPage';
import "@/App.scss";
import MyNotificationsPage from './pages/Notifications/MyNotificationsPage';
import { InsightsPanelProvider } from './contexts/InsightsPanelContext';
import LegacySignatureFormPage from "@/pages/Signatures/LegacySignatureFormPage";
import AllowedDomainsPage from './pages/Teams/AllowedDomainsPage';
import AdminHome from './pages/Admin/AdminHome';
import AdminEmailApproval from './pages/Admin/AdminEmailApproval';
import AdminRoleManagement from './pages/Admin/AdminRoleManagement';
import { AdminReports } from './pages/Admin/AdminReports';
import { AdminReportCatalog } from './pages/Admin/AdminReportCatalog';
import AdminAllReviews from './pages/Admin/AdminAllReviews';
import AdminReviewDetail from './pages/Admin/AdminReviewDetail';
import ReviewTemplateEditor from './pages/Admin/ReviewTemplateEditor';
import AdminAllReviewSets from './pages/Admin/AdminAllReviewSets';
import AdminReviewSetTemplates from './pages/Admin/AdminReviewSetTemplates';
import ReviewSetTemplateEditor from './pages/Admin/ReviewSetTemplateEditor';
import AssignReviewSet from './pages/Admin/AssignReviewSet';
import PimPage from './pages/Admin/PimPage';
import AutomatedElevationsPage from './pages/Admin/AutomatedElevationsPage';
import FormApprovalRequests from './pages/Admin/FormApprovalRequests';
import ResourceComplianceQuestionsAdmin from './pages/Admin/ResourceComplianceQuestionsAdmin';
import EmployeeOfferManagement from './pages/Admin/EmployeeOffer/EmployeeOfferManagement';
import UserAdminHome from './pages/Admin/UserAdmin/UserAdminHome';
import SpAccountPurgePage from './pages/Admin/UserAdmin/SpAccountPurgePage';
import EntraUsersPage from './pages/Admin/UserAdmin/EntraUsersPage';
import EmployeeHierarchyPage from './pages/Admin/UserAdmin/EmployeeHierarchyPage';
import UserDirectoryDetailPage from './pages/Admin/UserAdmin/UserDirectoryDetailPage';
import EmployeeHierarchyChangesPage from './pages/Admin/UserAdmin/EmployeeHierarchyChangesPage';
import AdminGroupsTabbed from './pages/Admin/AdminGroupsTabbed';
import AdminSiteSelectedProvision from './pages/Admin/AdminSiteSelectedProvision';
import AdminOutOfBandBoard from './pages/Admin/AdminOutOfBandBoard';
import AdminGroupDetail from './pages/Admin/AdminGroupDetail';
import AdminCheckSitePermissions from './pages/Admin/AdminCheckSitePermissions';
import FormsManagementPage from './pages/Admin/FormsManagement/FormsManagementPage';
import FormDetailPage from './pages/Admin/FormsManagement/FormDetailPage';
import OobStatusBoard from './pages/OobStatusBoard';
import EmailFilingHome from './pages/EmailFiling/EmailFilingHome';
import OobUserHome from './pages/OobUserHome';
import AdminOobEventPreview from './pages/Admin/AdminOobEventPreview';
import OobBreakGlassAdmin from './pages/OobBreakGlassAdmin';
import { OobExternalEntry } from './pages/OobExternalEntry';
import MyReviews from './pages/MyReviews';
import ReviewDetail from './pages/ReviewDetail';
import MyReviewSets from './pages/MyReviewSets';
import ReviewSetDetail from './pages/ReviewSetDetail';
import AssignReview from './pages/AssignReview';
import EmployeeOfferRegister from './pages/EmployeeOffer/EmployeeOfferRegister';
import DelegatedWorkInbox from './pages/DelegatedWorkInbox';
import DelegationDetail from './pages/DelegationDetail';
import WizardStart from './pages/WorkOS/WizardStart';
import WorkspaceHome from './pages/WorkOS/WorkspaceHome';
import LeaderWizard from './pages/WorkOS/LeaderWizard';
import WhatsNewPage from './pages/WhatsNew/WhatsNewPage';
import PhysicalObjectBrowser from './pages/PhysicalRecords/PhysicalObjectBrowser';
import PhysicalObjectDetail from './pages/PhysicalRecords/PhysicalObjectDetail';
import AdminPhysicalRecordsHome from './pages/Admin/PhysicalRecords/AdminPhysicalRecordsHome';
import AdminPhysicalRecordsCategories from './pages/Admin/PhysicalRecords/AdminPhysicalRecordsCategories';
import AdminPhysicalRecordsRequests from './pages/Admin/PhysicalRecords/AdminPhysicalRecordsRequests';
import AdminPhysicalRecordsDisposition from './pages/Admin/PhysicalRecords/AdminPhysicalRecordsDisposition';
import AdminPhysicalRecordsMigrationImport from './pages/Admin/PhysicalRecords/AdminPhysicalRecordsMigrationImport';
import SiteCoveragePage from './pages/Admin/SharePointInventory/SiteCoveragePage';
import FileExplorerPage from './pages/Admin/SharePointInventory/FileExplorerPage';
import SiteDetailPage from './pages/Admin/SharePointInventory/SiteDetailPage';
import CrawlHealthPage from './pages/Admin/SharePointInventory/CrawlHealthPage';
import { APP_NAME, upsertMetaTag } from '@/utils/pageMeta';

const POST_LOGIN_REDIRECT_KEY = 'oob_post_login_redirect';

const routeTitles: Array<{ path: string; title: string }> = [
  { path: '/home', title: 'Home' },
  { path: '/my-activity-log', title: 'My Activity Log' },
  { path: '/my-groups', title: 'My Groups' },
  { path: '/notifications', title: 'Notifications' },
  { path: '/resources', title: 'Resources' },
  { path: '/resources/:graphId', title: 'Resources' },
  { path: '/resource-compliance', title: 'Microsoft 365 Resource Health' },
  { path: '/resource-compliance/demo', title: 'Resource Health Demo' },
  { path: '/whats-new', title: "What's New" },
  { path: '/reviews', title: 'Reviews' },
  { path: '/reviews/:reviewGuid', title: 'Review Details' },
  { path: '/review-sets', title: 'Review Sets' },
  { path: '/review-sets/:setInstanceGuid', title: 'Review Set Details' },
  { path: '/delegations', title: 'Delegations' },
  { path: '/delegations/:guid', title: 'Delegation Details' },
  { path: '/workspaces', title: 'Workspaces' },
  { path: '/workspaces/:workspaceId', title: 'Workspace' },
  { path: '/workspaces/wizard', title: 'Workspace Wizard' },
  { path: '/physical-records', title: 'Browse Physical Records' },
  { path: '/physical-records/browse', title: 'Browse Physical Records' },
  { path: '/physical-records/:physicalObjectId', title: 'Physical Record Details' },
  { path: '/admin/physical-records', title: 'Physical Records Admin' },
  { path: '/admin/physical-records/categories', title: 'Physical Records Categories' },
  { path: '/admin/physical-records/requests', title: 'Physical Records Requests' },
  { path: '/admin/physical-records/disposition', title: 'Box Disposition Report' },
  { path: '/admin/physical-records/migration', title: 'Migrate Legacy InfoLink Data' },
  { path: '/admin', title: 'Admin' },
  { path: '/admin/assign-review', title: 'Assign Review' },
  { path: '/admin/reviews/templates/new', title: 'New Review Template' },
  { path: '/admin/reviews/templates/:templateGuid/edit', title: 'Edit Review Template' },
  { path: '/admin/review-sets', title: 'Review Sets' },
  { path: '/admin/review-sets/assign', title: 'Assign Review Set' },
  { path: '/admin/review-sets/templates', title: 'Review Set Templates' },
  { path: '/admin/review-sets/templates/new', title: 'New Review Set Template' },
  { path: '/admin/review-sets/templates/:setTemplateGuid/edit', title: 'Edit Review Set Template' },
  { path: '/admin/reviews', title: 'All Reviews' },
  { path: '/admin/reviews/:reviewGuid', title: 'Admin Review Details' },
  { path: '/admin/email-approval', title: 'Admin Email Approval' },
  { path: '/admin/roles', title: 'Admin Role Management' },
  { path: '/admin/reports', title: 'Admin Reports' },
  { path: '/admin/reports/catalog', title: 'M365 Reports' },
  { path: '/admin/pim', title: 'PIM' },
  { path: '/admin/pim/automated-elevations', title: 'Automated Elevations' },
  { path: '/admin/form-approvals', title: 'Form Approval Requests' },
  { path: '/admin/resource-compliance-questions', title: 'Resource Health Questions' },
  { path: '/admin/forms', title: 'Forms Management' },
  { path: '/admin/forms/:formId', title: 'Form Details' },
  { path: '/admin/employee-offers', title: 'Employee Offer Management' },
  { path: '/admin/users', title: 'User Admin' },
  { path: '/admin/users/sp-account-purge', title: 'SharePoint Account Purge' },
  { path: '/admin/users/entra-users', title: 'Entra Users' },
  { path: '/admin/users/employee-hierarchy', title: 'Employee Hierarchy' },
  { path: '/admin/users/employee-hierarchy-changes', title: 'Employee Change History' },
  { path: '/admin/users/details/:identifier', title: 'User Details' },
  { path: '/admin/groups', title: 'Groups Admin' },
  { path: '/admin/groups/check-site-permissions', title: 'Check Site Permissions' },
  { path: '/M365/admin/CheckSitePermissions', title: 'Check Site Permissions' },
  { path: '/admin/groups/:siteGuid', title: 'Group Storage Detail' },
  { path: '/admin/site-selected', title: 'Site.Selected Apps' },
  { path: '/admin/oob', title: 'OOB Status Board' },
  { path: '/admin/oob/breakglass', title: 'OOB Break-Glass Admin' },
  { path: '/oob', title: 'Out of Band' },
  { path: '/oob/onboarding', title: 'Out of Band' },
  { path: '/oob/:eventGuid', title: 'OOB Event Details' },
  { path: '/external', title: 'Emergency Access' },
  { path: '/EmployeeOffer/Register', title: 'Employee Offer Registration' },
  { path: '/Teams/AllowedDomains', title: 'Allowed Domains' },
  { path: '/signatures/GetSignature', title: 'Signature Form' },
  { path: '/admin/sharepoint/inventory', title: 'SharePoint Inventory' },
  { path: '/admin/sharepoint/inventory/files', title: 'File Explorer' },
  { path: '/admin/sharepoint/inventory/sites/:siteCollectionId', title: 'Site Details' },
  { path: '/admin/sharepoint/inventory/crawls', title: 'Crawl Health' },
];

const getPageTitle = (pathname: string): string => {
  const titleEntry = routeTitles.find((route) =>
    matchPath({ path: route.path, end: true }, pathname)
  );
  return titleEntry ? `${titleEntry.title} | ${APP_NAME}` : APP_NAME;
};

const LegacyGroupRedirect: React.FC = () => {
  const [params] = useSearchParams();
  const groupId = (params.get('g') || '').trim();
  const nextParams = new URLSearchParams();
  params.forEach((value, key) => {
    if (key !== 'g' && value.trim()) nextParams.append(key, value);
  });
  if (!groupId) return <Navigate to="/resources" replace />;
  const query = nextParams.toString();
  return <Navigate to={`/resources/${encodeURIComponent(groupId)}${query ? `?${query}` : ''}`} replace />;
};

const LegacyPhysicalRecordRedirect: React.FC = () => {
  const { physicalObjectId } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/physical-records/${encodeURIComponent(physicalObjectId ?? '')}${search}${hash}`} replace />;
};

const AppContent: React.FC = () => {
  const { accounts, instance, inProgress } = useMsal();
  const [initialized, setInitialized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const hasM365Account = !!accounts && accounts.length > 0;
  const hasValidAdminSession = (() => {
    const raw = localStorage.getItem("oob-admin-session");
    if (!raw) return false;
    try {
      const parsed = JSON.parse(raw);
      return new Date(parsed.sessionExpiresAt) > new Date();
    } catch {
      return false;
    }
  })();

  const { accent, mode } = useTheme();
  useEffect(() => {
    document.documentElement.setAttribute("data-accent", accent);
    document.documentElement.setAttribute("data-mode", mode);
  }, [accent, mode]);

  useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    document.title = pageTitle;
    upsertMetaTag('meta[property="og:title"]', 'property', 'og:title', pageTitle);
    upsertMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', pageTitle);
  }, [location.pathname]);

  // Check if current route is public (doesn't require M365 auth)
  // /external is always public (emergency token entry)
  // /oob is public (smart page - shows announcements if not logged in, M365 events if logged in)
  // /oob/{eventGuid} is public (smart page - shows event based on auth state)
  // /oob/onboarding (or /oob?onboarding=1) always forces Microsoft sign-in, bypassing the public/external flow
  const isAdminOobRoute = /^\/admin\/oob(\/[^/]+)?$/.test(location.pathname);
  const isBreakGlassRoute = location.pathname === '/admin/oob/breakglass';
  const searchParams = new URLSearchParams(location.search);
  const forceOnboardingAuth = location.pathname === '/oob/onboarding' ||
                              (location.pathname === '/oob' && searchParams.get('onboarding') === '1');

  const isPublicRoute = (((location.pathname === '/oob') || /^\/oob\/[^/]+$/.test(location.pathname)) && !hasM365Account && !forceOnboardingAuth) ||
                        location.pathname.startsWith('/external') || 
                        (isBreakGlassRoute && !hasM365Account) ||
                        (hasValidAdminSession && isAdminOobRoute && !hasM365Account); // break-glass fallback only when M365 auth is unavailable

  // Ensure active account is set once MSAL is initialized (unless on public route)
  useEffect(() => {
    if (!isPublicRoute) {
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount && accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }
    }
  }, [accounts, instance, isPublicRoute]);

  useEffect(() => {
    if (inProgress === "none") {
      setInitialized(true);
    }
  }, [inProgress]);

  const doLogin = async () => {
    if (!accounts || accounts.length === 0) {
      if (forceOnboardingAuth) {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, '/oob');
      }
      await instance.loginRedirect({
        scopes: ['User.Read', ...apiScopes],
        redirectUri: window.location.origin,
      });
    }
  };

  useEffect(() => {
    // Skip login redirect for public routes
    if (isPublicRoute) {
      return;
    }

    if (initialized && inProgress === "none" && (!accounts || accounts.length === 0)) {
      doLogin();
    }
  }, [initialized, inProgress, accounts, instance, isPublicRoute]);

  // After a forced-onboarding sign-in completes, land the user back on the OOB portal
  useEffect(() => {
    if (initialized && inProgress === "none" && hasM365Account) {
      const redirectTo = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (redirectTo) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        navigate(redirectTo, { replace: true });
      }
    }
  }, [initialized, inProgress, hasM365Account, navigate]);

  // Allow public routes to proceed without M365 auth
  if (isPublicRoute) {
    return (
      <OobSessionProvider>
        <Routes>
          <Route path="/external" element={<OobExternalEntry />} />
          <Route path="/oob" element={<OobUserHome />} />
          <Route path="/oob/:eventGuid" element={<OobStatusBoard />} />
          <Route path="/admin/oob/breakglass" element={<OobBreakGlassAdmin />} />
          {hasValidAdminSession && <Route path="/admin/oob" element={<AdminOutOfBandBoard />} />}
          {hasValidAdminSession && <Route path="/admin/oob/:eventGuid" element={<AdminOobEventPreview />} />}
          <Route path="*" element={<Navigate to="/external" replace />} />
        </Routes>
      </OobSessionProvider>
    );
  }

  if (!initialized || !accounts || accounts.length === 0) {
    return <p>Redirecting to login...</p>;
  }

  return (
    <OobSessionProvider>
      <InsightsPanelProvider>
        <RoleAccessProvider>
          <Layout
            hideSidebar={!!matchPath({ path: '/resource-compliance/demo', end: true }, location.pathname)}
            hideBreadcrumbs={!!matchPath({ path: '/resource-compliance/demo', end: true }, location.pathname)}
          >
            <Routes>
          {/* Root */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<Home />} />
          <Route path="/my-activity-log" element={<MyActivityLog />} />
          <Route path="/my-groups" element={<MyGroups />} />
          <Route path="/notifications" element={<MyNotificationsPage />} />

          {/* Email Filing */}
          <Route path="/email-filing" element={<EmailFilingHome />} />

          {/* Resources */}
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/:graphId" element={<ResourcesPage />} />
          <Route path="/resource-compliance" element={<ResourceCompliancePage />} />
          <Route path="/resource-compliance/demo" element={<ResourceHealthDemoPage />} />
          <Route path="/whats-new" element={<WhatsNewPage />} />
          <Route path="/M365/MyGroups" element={<Navigate to="/resources" replace />} />
          <Route path="/M365/Group" element={<LegacyGroupRedirect />} />

          {/* Reviews */}
          <Route path="/reviews" element={<MyReviews />} />
          <Route path="/reviews/:reviewGuid" element={<ReviewDetail />} />
          <Route path="/review-sets" element={<MyReviewSets />} />
          <Route path="/review-sets/:setInstanceGuid" element={<ReviewSetDetail />} />

          {/* Delegations */}
          <Route path="/delegations" element={<RequireSolutionAccess solution="delegations"><DelegatedWorkInbox /></RequireSolutionAccess>} />
          <Route path="/delegations/:guid" element={<RequireSolutionAccess solution="delegations"><DelegationDetail /></RequireSolutionAccess>} />

          {/* WorkOS / Workspaces */}
          <Route path="/workspaces" element={<RequireSolutionAccess solution="workspaces"><WizardStart /></RequireSolutionAccess>} />
          <Route path="/workspaces/wizard" element={<RequireSolutionAccess solution="workspaces"><LeaderWizard /></RequireSolutionAccess>} />
          <Route path="/workspaces/:workspaceId" element={<RequireSolutionAccess solution="workspaces"><WorkspaceHome /></RequireSolutionAccess>} />

          {/* Physical Records */}
          <Route path="/physical-records" element={<RequireSolutionAccess solution="physicalRecords"><PhysicalObjectBrowser /></RequireSolutionAccess>} />
          <Route path="/physical-records/browse" element={<RequireSolutionAccess solution="physicalRecords"><PhysicalObjectBrowser /></RequireSolutionAccess>} />
          <Route path="/physical-records/:physicalObjectId" element={<RequireSolutionAccess solution="physicalRecords"><PhysicalObjectDetail /></RequireSolutionAccess>} />
          <Route path="/physical-records/objects/:physicalObjectId" element={<LegacyPhysicalRecordRedirect />} />

          {/* Admin — home */}
          <Route path="/admin" element={<RequireAnySolutionAccess solutions={[
            'reviews',
            'emailApprovals',
            'roleManagement',
            'reports',
            'pim',
            'automatedElevations',
            'forms',
            'employeeOffers',
            'groupsAdmin',
            'outOfBand',
            'siteSelected',
            'userAdmin',
            'physicalRecordsAdmin',
          ]}><AdminHome /></RequireAnySolutionAccess>} />

          {/* Admin — Reviews */}
          <Route path="/admin/assign-review" element={<RequireSolutionAccess solution="reviews"><AssignReview /></RequireSolutionAccess>} />
          <Route path="/admin/reviews" element={<RequireSolutionAccess solution="reviews"><AdminAllReviews /></RequireSolutionAccess>} />
          <Route path="/admin/reviews/:reviewGuid" element={<RequireSolutionAccess solution="reviews"><AdminReviewDetail /></RequireSolutionAccess>} />
          <Route path="/admin/reviews/templates/new" element={<RequireSolutionAccess solution="reviews"><ReviewTemplateEditor /></RequireSolutionAccess>} />
          <Route path="/admin/reviews/templates/:templateGuid/edit" element={<RequireSolutionAccess solution="reviews"><ReviewTemplateEditor /></RequireSolutionAccess>} />

          {/* Admin — Review Sets */}
          <Route path="/admin/review-sets" element={<RequireSolutionAccess solution="reviews"><AdminAllReviewSets /></RequireSolutionAccess>} />
          <Route path="/admin/review-sets/assign" element={<RequireSolutionAccess solution="reviews"><AssignReviewSet /></RequireSolutionAccess>} />
          <Route path="/admin/review-sets/templates" element={<RequireSolutionAccess solution="reviews"><AdminReviewSetTemplates /></RequireSolutionAccess>} />
          <Route path="/admin/review-sets/templates/new" element={<RequireSolutionAccess solution="reviews"><ReviewSetTemplateEditor /></RequireSolutionAccess>} />
          <Route path="/admin/review-sets/templates/:setTemplateGuid/edit" element={<RequireSolutionAccess solution="reviews"><ReviewSetTemplateEditor /></RequireSolutionAccess>} />

          {/* Admin — other */}
          <Route path="/admin/email-approval" element={<RequireSolutionAccess solution="emailApprovals"><AdminEmailApproval /></RequireSolutionAccess>} />
          <Route path="/admin/roles" element={<RequireSolutionAccess solution="roleManagement"><AdminRoleManagement /></RequireSolutionAccess>} />
          <Route path="/admin/reports" element={<RequireSolutionAccess solution="reports"><AdminReports /></RequireSolutionAccess>} />
          <Route path="/admin/reports/catalog" element={<RequireSolutionAccess solution="reports"><AdminReportCatalog /></RequireSolutionAccess>} />
          <Route path="/admin/pim" element={<RequireSolutionAccess solution="pim"><PimPage /></RequireSolutionAccess>} />
          <Route path="/admin/pim/automated-elevations" element={<RequireSolutionAccess solution="automatedElevations"><AutomatedElevationsPage /></RequireSolutionAccess>} />
          <Route path="/admin/form-approvals" element={<RequireSolutionAccess solution="forms"><FormApprovalRequests /></RequireSolutionAccess>} />
          <Route path="/admin/resource-compliance-questions" element={<RequireSolutionAccess solution="forms"><ResourceComplianceQuestionsAdmin /></RequireSolutionAccess>} />
          <Route path="/admin/forms" element={<RequireSolutionAccess solution="forms"><FormsManagementPage /></RequireSolutionAccess>} />
          <Route path="/admin/forms/:formId" element={<RequireSolutionAccess solution="forms"><FormDetailPage /></RequireSolutionAccess>} />
          <Route path="/admin/employee-offers" element={<RequireSolutionAccess solution="employeeOffers"><EmployeeOfferManagement /></RequireSolutionAccess>} />
          <Route path="/admin/users" element={<RequireSolutionAccess solution="userAdmin"><UserAdminHome /></RequireSolutionAccess>} />
          <Route path="/admin/users/sp-account-purge" element={<RequireSolutionAccess solution="userAdmin"><SpAccountPurgePage /></RequireSolutionAccess>} />
          <Route path="/admin/users/entra-users" element={<RequireSolutionAccess solution="userAdmin"><EntraUsersPage /></RequireSolutionAccess>} />
          <Route path="/admin/users/employee-hierarchy" element={<RequireSolutionAccess solution="userAdmin"><EmployeeHierarchyPage /></RequireSolutionAccess>} />
          <Route path="/admin/users/employee-hierarchy-changes" element={<RequireSolutionAccess solution="userAdmin"><EmployeeHierarchyChangesPage /></RequireSolutionAccess>} />
          <Route path="/admin/users/details/:identifier" element={<RequireSolutionAccess solution="userAdmin"><UserDirectoryDetailPage /></RequireSolutionAccess>} />
          <Route path="/admin/user-admin" element={<Navigate to="/admin/users" replace />} />
          <Route path="/admin/user-admin/sp-account-purge" element={<Navigate to="/admin/users/sp-account-purge" replace />} />
          <Route path="/admin/groups" element={<RequireSolutionAccess solution="groupsAdmin"><AdminGroupsTabbed /></RequireSolutionAccess>} />
          <Route path="/admin/groups/check-site-permissions" element={<RequireSolutionAccess solution="groupsAdmin"><AdminCheckSitePermissions /></RequireSolutionAccess>} />
          <Route path="/M365/admin/CheckSitePermissions" element={<RequireSolutionAccess solution="groupsAdmin"><AdminCheckSitePermissions /></RequireSolutionAccess>} />
          <Route path="/admin/groups/:siteGuid" element={<RequireSolutionAccess solution="groupsAdmin"><AdminGroupDetail /></RequireSolutionAccess>} />
          <Route path="/admin/oob" element={<RequireSolutionAccess solution="outOfBand"><AdminOutOfBandBoard /></RequireSolutionAccess>} />
          <Route path="/admin/oob/:eventGuid" element={<RequireSolutionAccess solution="outOfBand"><AdminOobEventPreview /></RequireSolutionAccess>} />
          <Route path="/admin/oob/breakglass" element={<Navigate to="/admin/oob" replace />} />
          <Route path="/admin/site-selected" element={<RequireSolutionAccess solution="siteSelected"><AdminSiteSelectedProvision /></RequireSolutionAccess>} />
          <Route path="/admin/physical-records" element={<RequireSolutionAccess solution="physicalRecordsAdmin"><AdminPhysicalRecordsHome /></RequireSolutionAccess>} />
          <Route path="/admin/physical-records/categories" element={<RequireSolutionAccess solution="physicalRecordsAdmin"><AdminPhysicalRecordsCategories /></RequireSolutionAccess>} />
          <Route path="/admin/physical-records/requests" element={<RequireSolutionAccess solution="physicalRecordsAdmin"><AdminPhysicalRecordsRequests /></RequireSolutionAccess>} />
          <Route path="/admin/physical-records/disposition" element={<RequireSolutionAccess solution="physicalRecordsAdmin"><AdminPhysicalRecordsDisposition /></RequireSolutionAccess>} />
          <Route path="/admin/physical-records/migration" element={<RequireSolutionAccess solution="physicalRecordsAdmin"><AdminPhysicalRecordsMigrationImport /></RequireSolutionAccess>} />
          <Route path="/admin/sharepoint/inventory" element={<RequireSolutionAccess solution="sharePointInventory"><SiteCoveragePage /></RequireSolutionAccess>} />
          <Route path="/admin/sharepoint/inventory/files" element={<RequireSolutionAccess solution="sharePointInventory"><FileExplorerPage /></RequireSolutionAccess>} />
          <Route path="/admin/sharepoint/inventory/sites/:siteCollectionId" element={<RequireSolutionAccess solution="sharePointInventory"><SiteDetailPage /></RequireSolutionAccess>} />
          <Route path="/admin/sharepoint/inventory/crawls" element={<RequireSolutionAccess solution="sharePointInventory"><CrawlHealthPage /></RequireSolutionAccess>} />

          {/* OOB — logged-in users should render inside app layout */}
          <Route path="/oob" element={<OobUserHome />} />
          <Route path="/oob/onboarding" element={<Navigate to="/oob" replace />} />
          <Route path="/oob/:eventGuid" element={<OobStatusBoard />} />

          {/* Legacy / misc */}
          <Route path="/signatures/GetSignature" element={<LegacySignatureFormPage />} />
          <Route path="/Teams/AllowedDomains" element={<AllowedDomainsPage />} />
          <Route path="/EmployeeOffer/Register" element={<EmployeeOfferRegister />} />
          </Routes>
        </Layout>
      </RoleAccessProvider>
    </InsightsPanelProvider>
    </OobSessionProvider>
  );
};

const App: React.FC = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
